import React, { useEffect, useMemo, useState } from 'react';
import InterventionPointSelectionHeader from './InterventionPointSelectionHeader';
import IPStatsLanding from './IPStatsLanding';
import baseFetchWithAuth from '../../utils/fetchWithAuth';
import { API_BASE_URL } from '../../config';
import { useInterventionPoint } from '../../contexts/InterventionPointContext';
import { formatRelativeDate, projectLabel, getInitial } from './ipHelpers';

const SORT_OPTIONS = [
  { value: 'visits', label: 'Visites' },
  { value: 'mobiliers', label: 'Mobiliers' },
  { value: 'lastVisit', label: 'Dernière visite' },
  { value: 'anomalies', label: 'Anomalies' },
];

// Search shortcuts that show a full landing-stats category as a result list
const LIST_DEFS = {
  last_visit: { statsKey: 'lastVisited', title: 'Derniers magasins visités' },
  open_issues: { statsKey: 'topOpen', title: "Magasins avec interventions ouvertes" },
  perfect_store: { statsKey: 'recentlyPerfect', title: 'Perfect Store récemment retrouvé' },
};

const StatusBadge = ({ openCount }) => {
  if (openCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        {openCount} ouverte{openCount > 1 ? 's' : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      À jour
    </span>
  );
};

const ProjectPellets = ({ projects = [], max = 2 }) => {
  if (!projects.length) return null;
  const shown = projects.slice(0, max);
  const extra = projects.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {shown.map((proj, idx) => (
        <span
          key={idx}
          className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
        >
          {projectLabel(proj)}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
          +{extra}
        </span>
      )}
    </div>
  );
};

const Counter = ({ value, label }) => (
  <div className="text-right">
    <span className="text-gray-400 dark:text-gray-500 tabular-nums">{value}</span>{' '}
    <span className="text-gray-400 dark:text-gray-500">{label}</span>
  </div>
);

function IPCard({ ip, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(ip)}
      className="w-full text-left bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all flex gap-3"
    >
      <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-semibold text-gray-600 dark:text-gray-300">
        {getInitial(ip.Name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-800 dark:text-white truncate">{ip.Name}</h3>
              {ip.ShopID && (
                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  SAP {ip.ShopID}
                </span>
              )}
            </div>
            {ip.Details && (
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{ip.Details}</p>
            )}
          </div>
          <StatusBadge openCount={ip.openCount || 0} />
        </div>

        <div className="flex items-end justify-between mt-2">
          <ProjectPellets projects={ip.Projects} />
          <div className="text-xs space-y-0.5 shrink-0 pl-3">
            <Counter value={ip.CallCount} label={`visite${ip.CallCount > 1 ? 's' : ''}`} />
            <Counter value={ip.furnituresCount} label={`mobilier${ip.furnituresCount > 1 ? 's' : ''}`} />
            {ip.lastVisitDate && (
              <div className="text-right text-gray-400 dark:text-gray-500">{formatRelativeDate(ip.lastVisitDate)}</div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function IPSearchList({ jail }) {
  const {
    interventionPoints,
    filteredInterventionPoints,
    loading,
    searchQuery,
    setSearchQuery,
    projets,
    selectedProjet,
    setSelectedProjet,
    setSelectedInterventionPoint,
  } = useInterventionPoint();

  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  const [sortBy, setSortBy] = useState('visits');

  // Cross-program landing stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  // Nothing is fetched until a project is selected — landing stats included.
  useEffect(() => {
    if (!selectedProjet?.value) {
      setStats(null);
      setStatsLoading(false);
      setStatsError(false);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        setStatsLoading(true);
        const res = await baseFetchWithAuth(`${API_BASE_URL}/api/dwh/one_mag/stats?projectId=${selectedProjet.value}`, { signal: controller.signal });
        const data = res && typeof res.json === 'function' ? await res.json() : res;
        if (!controller.signal.aborted) setStats(data);
      } catch (e) {
        if (!controller.signal.aborted) setStatsError(true);
      } finally {
        if (!controller.signal.aborted) setStatsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [selectedProjet]);

  // Open a store (cross-program aware): set its project then select it
  const openStore = (item) => {
    const proj =
      projets.find((p) => String(p.value) === String(item.projectId)) ||
      (item.projectId ? { value: item.projectId, label: item.projectId === 172 ? 'Dyson' : "L'Oréal LUXE" } : null);
    if (proj) setSelectedProjet(proj);
    setSelectedInterventionPoint(item);
  };

  // `list:` shortcut detection
  const listKey = useMemo(() => {
    const m = searchQuery.trim().match(/^list:(last_visit|open_issues|perfect_store)$/i);
    return m ? m[1].toLowerCase() : null;
  }, [searchQuery]);

  const results = useMemo(() => {
    let list = [...filteredInterventionPoints];
    if (anomaliesOnly) list = list.filter((ip) => (ip.openCount || 0) > 0);

    const cmp = {
      visits: (a, b) => (b.CallCount || 0) - (a.CallCount || 0),
      mobiliers: (a, b) => (b.furnituresCount || 0) - (a.furnituresCount || 0),
      anomalies: (a, b) => (b.openCount || 0) - (a.openCount || 0),
      lastVisit: (a, b) => new Date(b.lastVisitDate || 0) - new Date(a.lastVisitDate || 0),
    }[sortBy];

    return cmp ? list.sort(cmp) : list;
  }, [filteredInterventionPoints, anomaliesOnly, sortBy]);

  const showResults = searchQuery.length >= 3 && !listKey;

  return (
    <div className="container mx-auto p-4">
      <InterventionPointSelectionHeader jail={jail} />

      {!selectedProjet ? (
        // ---- No project yet: fetch nothing, prompt the user to pick one --------
        <div className="flex flex-col items-center justify-center text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-10 mt-2">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-3H5a2 2 0 0 0-2 2z" />
            </svg>
          </span>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Aucun projet sélectionné</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Sélectionnez un projet ci-dessus pour charger les magasins et les statistiques.
          </p>
        </div>
      ) : listKey ? (
        // ---- "Voir tout" category list -------------------------------------
        statsLoading ? (
          <div className="flex justify-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          (() => {
            const def = LIST_DEFS[listKey];
            const items = (stats && stats[def.statsKey]) || [];
            return (
              <>
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    {def.title} <span className="text-sm font-normal text-gray-400 dark:text-gray-500">({items.length})</span>
                  </h2>
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Effacer
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {items.map((ip, index) => (
                    <IPCard key={`${ip.Name}-${index}`} ip={ip} onSelect={openStore} />
                  ))}
                  {items.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 dark:text-gray-400 py-8">
                      Aucun magasin dans cette catégorie.
                    </div>
                  )}
                </div>
              </>
            );
          })()
        )
      ) : !showResults ? (
        // ---- Landing stats -------------------------------------------------
        <IPStatsLanding
          stats={stats}
          loading={statsLoading}
          error={statsError}
          onOpen={openStore}
          onShowMore={(key) => setSearchQuery(`list:${key}`)}
        />
      ) : loading ? (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      ) : (
        // ---- Text search results -------------------------------------------
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {`${results.length} résultat${results.length > 1 ? 's' : ''}`}
              {interventionPoints.length > 0 && (
                <span className="text-gray-400 dark:text-gray-500"> · {interventionPoints.length} points chargés</span>
              )}
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAnomaliesOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                  anomaliesOnly
                    ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" />
                </svg>
                Anomalies
              </button>

              <div className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4 4m0 0l4-4m-4 4V4" />
                </svg>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent focus:outline-none cursor-pointer dark:text-gray-300"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="dark:bg-gray-800">
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                title="Effacer la recherche"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Effacer
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.map((ip, index) => (
              <IPCard key={`${ip.Name}-${index}`} ip={ip} onSelect={openStore} />
            ))}
            {results.length === 0 && (
              <div className="col-span-full text-center text-gray-500 dark:text-gray-400 py-8">
                Aucun résultat{anomaliesOnly ? ' avec anomalies' : ''} pour "{searchQuery}".
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
